import { useState, useEffect } from 'react'
import { useLogin } from '@refinedev/core'
import { useSearchParams } from 'react-router-dom'
import { Form, Input, Button, Card, Typography, Divider, Alert } from 'antd'
import { UserOutlined, LockOutlined, GoogleOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

interface LoginFormValues {
  email: string
  password: string
}

export default function LoginPage() {
  const { mutate: login, isPending } = useLogin<LoginFormValues>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  // Google OAuth login - redirect to API which handles Passport flow
  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/auth/admin/google`
  }

  // Check for OAuth error in URL params
  useEffect(() => {
    const errorParam = searchParams.get('error')
    if (errorParam) {
      setError(decodeURIComponent(errorParam))
      // Clear the error from URL
      searchParams.delete('error')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const onFinish = async (values: LoginFormValues) => {
    setError(null)
    login(values, {
      onError: (err) => {
        setError(err.message || 'Greska pri prijavljivanju')
      },
    })
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0d9488 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8, color: '#1e3a5f' }}>
            PerfectaMed
          </Title>
          <Text type="secondary">Admin Portal</Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        <Form<LoginFormValues>
          name="login"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: 'Unesite email' },
              { type: 'email', message: 'Unesite validan email' },
            ]}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Email adresa"
              size="large"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Unesite lozinku' }]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Lozinka"
              size="large"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isPending}
              style={{
                backgroundColor: '#1e3a5f',
                borderColor: '#1e3a5f',
              }}
            >
              Prijavite se
            </Button>
          </Form.Item>
        </Form>

        <Divider>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ili
          </Text>
        </Divider>

        <Button
          size="large"
          block
          icon={<GoogleOutlined />}
          onClick={handleGoogleLogin}
          disabled={isPending}
          style={{ marginBottom: 16 }}
        >
          Nastavite sa Google nalogom
        </Button>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Pristup je omogucen samo za ovlasceno osoblje.
            <br />
            Kontaktirajte administratora za pristup.
          </Text>
        </div>
      </Card>
    </div>
  )
}
